import { N3XUS_API, N3XUS_STAGE_API, N3XUS_API_LOCAL, N3XUS_DEVELOPMENT_API } from '../../constants/apis'
import {
    AccessCheckData,
    MintData,
    StatusData,
    ActivationData,
    N3xusApiConfig,
    N3xusApiSettings,
    CampaignStatusData, 
    CampaignInformationData,
    Environment
    //MintStatus
} from './types';
import {getErrorMessage} from '../../utils/helpers'

const formatDate = (date: Date) =>
	`${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')} ${String(
		date.getSeconds(),
	).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`

class N3xusApi 
{
    private n3xusSettings: null | N3xusApiSettings = null
    private url: string = ''
    private headers: Headers = new Headers()
    
    constructor(
        options: N3xusApiConfig 
    ) {
        if (options.apiKey.length < 5) {
            throw new Error('Invalid API key')
        }
        const key = options.apiKey.substring(5); // Extract the part after "pk-1-"
        const mid = Math.floor(key.length / 2)
        const user = key.substring(0, mid);
        const password = key.substring(mid);
        // Map environment to corresponding API configuration
        const environmentApiMap = {
            [Environment.PRODUCTION]: N3XUS_API,
            [Environment.STAGING]: N3XUS_STAGE_API,
            [Environment.DEVELOPMENT]: N3XUS_DEVELOPMENT_API,
            [Environment.LOCAL]: N3XUS_API_LOCAL
        };
        
        this.n3xusSettings = environmentApiMap[options.environment ?? Environment.LOCAL] ?? N3XUS_API_LOCAL;
        this.url = `${this.n3xusSettings.HOST}${this.n3xusSettings.ENDPOINT}${this.n3xusSettings.VERSION}`
        this.headers.set('Content-Type', 'application/json')
        if (this.n3xusSettings.ENV) {
            this.headers.set('X-Env', this.n3xusSettings.ENV)
        }
        this.headers.set('x-api-key', options.apiKey)
        this.headers.set('Authorization', `Basic ${btoa(`${user}:${password}`)}`)
    }

    public async checkIssueStatus(issueId: number| null, redeemCode: string | null): Promise<StatusData> {
        try {   
            let response
            if (issueId) {
                response = await fetch(
                    `${this.url}/public/issuance/?issueId=${issueId}`,
                    {
                        method: 'GET',
                        headers: this.headers,
                    }
                )
            } else if (redeemCode) {
                response = await fetch(
                    `${this.url}/public/issuance/?code=${redeemCode}`,
                    {
                        method: 'GET',
                        headers: this.headers,
                    }
                )
            } else {
                throw new Error("no valid issueId or redeemCode")
            }
             
            
            type JSONResponse = {
                data?: {
                    status: Omit<StatusData, 'fetchedAt'>
                }
                errors?: Array<{ message: string }>
            }
            
            const data: JSONResponse = await response.json()

            if (response.status === 200 && data) {
                return Object.assign(data, { fetchedAt: formatDate(new Date()), ok: true }) as StatusData
            } else if (response.status === 403) {
                return Object.assign({ ok: false, error: "Invalid API key" }, { fetchedAt: formatDate(new Date()), ok: false }) as StatusData
            } else {
                return Object.assign({ ok: false, error: "Status Error" }, { fetchedAt: formatDate(new Date()), ok: false }) as StatusData
            }
        } catch (error: unknown) {
            const errorMessage = getErrorMessage(error)
            return Object.assign({ ok: false, error: errorMessage }, { fetchedAt: formatDate(new Date()), ok: false }) as StatusData
        }
    }    
    public async checkWalletForNft(unlockMetadata:string, didToken: string): Promise<AccessCheckData> {
        try {
            const clonedHeaders = new Headers(this.headers);
            clonedHeaders.set('x-did-token', didToken);

            const params = new URLSearchParams();
            params.append('unlockMetadata', JSON.stringify(unlockMetadata));
            const response = await fetch(
                `${this.url}/public/wallet/holds?${params.toString()}`,
                {
                    method: 'GET',
                    headers: clonedHeaders,
                }
            )
            const data: AccessCheckData = await response.json()
            if (response.status === 200 && data && data.ok) {
                return Object.assign(data)
            } else {
                return Promise.reject(new Error(`Failed to fetch code status: ${data?.error}`))
            }
        } catch (error: unknown) {
            const errorMessage = getErrorMessage(error)
            return Promise.reject(new Error(`Error fetching code status:${errorMessage}`))
        }
    }

    public async mint(didToken: string, issuanceCode?: string, campaignUrl?: string, campaignId?: number): Promise<MintData> {
        try {
            let response 
            if (campaignId) {
                response = await fetch(
                    `${this.url}/public/issuance/mint`,
                    {
                        method: 'POST',
                        headers: this.headers,
                        body: JSON.stringify({
                            "didToken": didToken,
                            "campaignId": campaignId,
                        })
                    }
                )     
            }
            else if (issuanceCode == undefined) {
                response = await fetch(
                    `${this.url}/public/issuance/mint`,
                    {
                        method: 'POST',
                        headers: this.headers,
                        body: JSON.stringify({
                            "didToken": didToken,
                            "campaignUrl": campaignUrl,
                        })
                    }
                )     
            } else { 
                response = await fetch(
                    `${this.url}/public/issuance/mint`,
                    {
                        method: 'POST',
                        headers: this.headers,
                        body: JSON.stringify({
                            "didToken": didToken,
                            "issuanceCode": issuanceCode,
                        })
                    }
                ) 
            }
            type JSONResponse = {
                data?: Omit<MintData, 'fetchedAt'>
                error?: Array<{ message: string }>
                ok?: boolean
            }
            const { data, error , ok}: JSONResponse = await response.json()
            if (response.status === 200 && data && ok) {
                return Object.assign(data)
            } else {
                return Promise.reject(new Error(`Failed to fetch code status: ${error}`))
            }
        } catch (error: unknown) {
            const errorMessage = getErrorMessage(error)
            console.error(errorMessage)
            return Promise.reject(new Error(`Error fetching code status:${errorMessage}`))
        }
    }

    public async logActivation(contract_address: string, type: string, wallet_address: string): Promise<ActivationData> {
        try {
            const response = await fetch(
                `${this.url}/activation/log`,
                {
                    method: 'POST',
                    headers: this.headers,
                    body: JSON.stringify({
                        "contract_address": contract_address,
                        "type": type,
                        "wallet_address": wallet_address
                    })
                }
            )
            type JSONResponse = {
                data?: ActivationData
                errors?: Array<{ message: string }>
            }
    
            const { data, errors }: JSONResponse = await response.json()
            if (response.status === 200 && data) {
                return Object.assign(data)
            } else {
                return Promise.reject(new Error(`Failed to fetch code status: ${errors?.[0].message}`))
            }
        }
        catch (error: unknown) {
            const errorMessage = getErrorMessage(error)
            console.error(errorMessage)
            return Promise.reject(new Error(`Error fetching code status:${errorMessage}`))
        }
    }

    //This needs work have moved to any as needs more checks
    public async checkCampaign(campaignUrl: string): Promise<CampaignStatusData> {
        try {
            const response = await fetch(
                `${this.url}/public/issuance/campaign/availability/?campaign_url=${campaignUrl}`,
                {
                    method: 'GET',
                    headers: this.headers,
                }
            )

            const data = await response.json()

            return data
        } catch (err) {
            console.log(err)
            throw new Error('err')
        }
    } 

    public async getCampaignInformation(campaignId:number): Promise<CampaignInformationData>{
        try {
            const response = await fetch(
                `${this.url}/public/issuance/campaign/?campaignId=${campaignId}`,
                {
                    method: 'GET',
                    headers: this.headers,
                }
            )

            const data = await response.json()



            return data
        } catch (err) {
            console.log(err)
            throw new Error('err')
        }
    }
}

export default N3xusApi
